import { useState } from 'react';

import {
  ChevronRight,
  ExternalLink,
  MousePointerClick,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/shadcn/button';
import { Copy } from '@/shadcn/copy';
import { cn } from '@/shadcn/lib/utils';

import { ErrorPageImage } from '@/components/errorPage/errorPage.image';
import { TestId } from '@/types/testId';

type Props = {
  onHomeClick: () => void;
  onReloadPageClick?: () => void;
  title?: string;
  buttonLabel?: string;
  description?: React.ReactNode;
  errorMessage?: string;
};

export const ErrorPage = ({
  onHomeClick,
  onReloadPageClick,
  title,
  buttonLabel = 'Take me to the home page',
  description = 'Sorry, we encountered an error.',
  errorMessage
}: Props) => {
  const [isErrorExpanded, setIsErrorExpanded] = useState(false);

  const onCopyErrorClicked = () => {
    toast.success('Error copied to clipboard');
  };

  //? Note: We can't use the Navbar here since we're out of the router context
  return (
    <section className="scrollbar-stable flex size-full h-screen flex-col items-center overflow-auto bg-card md:mt-0 md:justify-start md:pt-[7%] dark:bg-background">
      <div className="flex w-full max-w-5xl flex-col items-center justify-center p-4">
        <ErrorPageImage className="mb-10 w-full max-w-[150px] select-none md:max-w-[200px]" />
        {title && (
          <div className="text-center text-xl">
            Error{' '}
            <span className="rounded-md border border-border bg-muted p-1 font-mono text-base dark:bg-card dark:text-card-foreground">
              {title}
            </span>
          </div>
        )}
        {description && <div className="pt-4 text-center">{description}</div>}
        <div className="mt-8 flex select-none flex-col items-center justify-center gap-1 md:flex-row md:gap-4">
          {onReloadPageClick && (
            <>
              <Button
                aria-label="Reload page"
                className="group/reload"
                data-testid={TestId.ErrorPage.ReloadButton}
                onClick={onReloadPageClick}
                variant="outline"
              >
                <RefreshCw className="transition-transform duration-500 ease-in-out group-hover/reload:rotate-[360deg]" />
                Try to reload the page
              </Button>
              <span className="text-md text-muted-foreground italic">or</span>
            </>
          )}
          <Button
            aria-label={buttonLabel ?? 'Take me home'}
            data-testid={TestId.ErrorPage.RedirectButton}
            onClick={onHomeClick}
          >
            {buttonLabel}
            <ExternalLink />
          </Button>
        </div>

        {errorMessage && (
          <div
            className={cn(
              'group/error relative mt-8 box-border flex h-24 w-full max-w-3xl justify-center gap-4 overflow-hidden text-wrap rounded-md border border-border bg-muted p-4 text-left text-muted-foreground text-xs leading-4.5 transition-all duration-300 ease-in-out dark:bg-card dark:text-card-foreground',
              isErrorExpanded && 'h-auto',
              !isErrorExpanded &&
                'cursor-pointer select-none pb-0 hover:brightness-95'
            )}
            data-testid={TestId.ErrorPage.ErrorMessage}
            onClick={() => setIsErrorExpanded(true)}
          >
            <p className="w-full overflow-hidden whitespace-break-spaces text-wrap text-left font-mono italic">
              <span className="mr-1 inline-block select-none align-sub">
                <ChevronRight size={14} />
              </span>
              <span>{errorMessage}</span>
            </p>
            {isErrorExpanded ? (
              <div className="absolute top-2 right-2 z-10 opacity-0 transition-opacity duration-150 ease-in-out group-hover/error:opacity-100">
                <Copy
                  onClick={onCopyErrorClicked}
                  textToCopy={errorMessage}
                  tooltipText="Click to copy the error message"
                />
              </div>
            ) : (
              <>
                <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-full bg-gradient-to-t from-background/90 to-transparent dark:from-card/90" />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 text-base opacity-0 backdrop-blur-none transition-all duration-300 ease-in-out group-hover/error:opacity-100 group-hover/error:backdrop-blur-[1px]">
                  <span>Click to open the error details</span>
                  <MousePointerClick />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
